import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthState } from '@auth/state/models/auth.models';
import { EnvironmentData } from '@auth/environment/models/environment-settings.model';

const millisecondsPerDay = 86400000; // 24 * 60 * 60 * 1000
const defaultCookieExpiryDays = 30;
const consentCookieExpiryDays = 365;
const cookieExpiryDate1970 = 'Thu, 01 Jan 1970 00:00:00 UTC';

const jwtCookieName = 'JWT';
const refreshTokenCookieName = 'rT';
const selectedEnvironmentCookieName = 'selectedEnvironment';

@Injectable({
  providedIn: 'root'
})
export class UnityCookieService {
  private readonly document = inject(DOCUMENT);
  private readonly consentSubject = new BehaviorSubject<boolean>(
    this.getCookie('unity-system-cookieConsent') === 'accepted'
  );

  setCookie(name: string, value: string, days = defaultCookieExpiryDays): void {
    const expires = new Date(Date.now() + days * millisecondsPerDay).toUTCString();
    this.document.cookie = `${name}=${value}; expires=${expires}; path=/; secure; SameSite=Strict`;
  }

  setSelectedEnvironment(environment: EnvironmentData): void {
    this.setCookie(selectedEnvironmentCookieName, JSON.stringify(environment));
  }

  getSelectedEnvironment(): string {
    const environment = this.getCookie(selectedEnvironmentCookieName);
    return environment ? JSON.parse(environment) : '';
  }

  getCookie(name: string): string {
    const cookieValue = this.document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1] ?? '';
    
    // Decode HTML-encoded values that may be present in cookies
    try {
      return decodeURIComponent(cookieValue);
    } catch {
      // If decoding fails, return the original value
      return cookieValue;
    }
  }

  cookieExists(name: string): boolean {
    return this.document.cookie
      .split('; ')
      .some(row => row.startsWith(name + '='));
  }

  deleteCookie(name: string): void {
    this.document.cookie = `${name}=; expires=${cookieExpiryDate1970}; path=/`;
  }

  getConsentState(): Observable<boolean> {
    return this.consentSubject.asObservable();
  }

  setJWT(token: string): void {
    this.setCookie(jwtCookieName, token);
  }

  getJWT(): string {
    return this.getCookie(jwtCookieName);
  }

  setRefreshToken(refreshToken: string): void {
    this.setCookie(refreshTokenCookieName, refreshToken);
  }

  getRefreshToken(): string {
    return this.getCookie(refreshTokenCookieName);
  }

  setAuthState(authState: AuthState): void {
    this.setJWT(authState.token);
    this.setRefreshToken(authState.refreshToken);
  }

  setConsentState(accepted: boolean): void {
    this.setCookie('unity-system-cookieConsent', accepted ? 'accepted' : 'declined', consentCookieExpiryDays);
    this.consentSubject.next(accepted);
  }

  clearAuthState(): void {
    this.deleteCookie(jwtCookieName);
    this.deleteCookie(refreshTokenCookieName);
  }
}
